import React from 'react';
import { Smartphone, Globe, Search, TrendingUp, Code, Database, Shield, Zap, Users, CheckCircle, ArrowRight, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const Services: React.FC = () => {
  const services = [
    {
      id: 'app-development',
      title: 'Mobile App Development',
      icon: <Smartphone className="h-12 w-12" />,
      description: 'Native and cross-platform mobile applications that deliver exceptional user experiences across iOS and Android platforms.',
      features: [
        'Native iOS & Android Development',
        'Cross-platform Solutions (React Native, Flutter)',
        'Progressive Web Apps (PWA)',
        'App Store Optimization',
        'Backend Integration',
        'Push Notifications',
        'In-app Purchases',
        'Analytics Integration'
      ],
      technologies: ['React Native', 'Flutter', 'Swift', 'Kotlin', 'Xamarin'],
      
      color: 'from-purple-500 to-pink-600'
    },
    {
      id: 'web-development',
      title: 'Web Development',
      icon: <Globe className="h-12 w-12" />,
      description: 'Modern, responsive websites and web applications built with cutting-edge technologies and best practices.',
      features: [
        'Custom Web Applications',
        'E-commerce Solutions',
        'Content Management Systems',
        'API Development & Integration',
        'Database Design & Optimization',
        'Cloud Deployment',
        'Performance Optimization',
        'Security Implementation'
      ],
      technologies: ['React', 'Node.js', 'Next.js', 'TypeScript', 'PostgreSQL'],
      
      color: 'from-blue-500 to-cyan-600'
    },
    {
      id: 'seo',
      title: 'SEO & Digital Marketing',
      icon: <Search className="h-12 w-12" />,
      description: 'Comprehensive SEO strategies and digital marketing solutions to boost your online visibility and drive organic traffic.',
      features: [
        'Technical SEO Audit',
        'Keyword Research & Strategy',
        'On-page Optimization',
        'Link Building Campaigns',
        'Local SEO',
        'Content Marketing',
        'Google Ads Management',
        'Analytics & Reporting'
      ],
      technologies: ['Google Analytics', 'SEMrush', 'Ahrefs', 'Google Search Console'],
      
      color: 'from-green-500 to-emerald-600'
    },
    {
      id: 'marketing-consultancy',
      title: 'Marketing Consultancy',
      icon: <TrendingUp className="h-12 w-12" />,
      description: 'Strategic marketing consultation to help businesses develop effective digital marketing strategies and grow their online presence.',
      features: [
        'Digital Strategy Development',
        'Brand Positioning',
        'Market Research & Analysis',
        'Competitor Analysis',
        'Social Media Strategy',
        'Email Marketing Campaigns',
        'Conversion Rate Optimization',
        'Marketing Automation'
      ],
      technologies: ['HubSpot', 'Mailchimp', 'Google Analytics', 'Facebook Ads'],
     
      color: 'from-orange-500 to-red-600'
    }
  ];

  const additionalServices = [
    {
      icon: <Code className="h-8 w-8" />,
      title: 'Custom Software Development',
      description: 'Tailored software solutions designed to meet your specific business requirements.'
    },
    {
      icon: <Database className="h-8 w-8" />,
      title: 'Database Design & Management',
      description: 'Efficient database architecture and management for optimal performance.'
    },
    {
      icon: <Shield className="h-8 w-8" />,
      title: 'Cybersecurity Solutions',
      description: 'Comprehensive security audits and implementation of security best practices.'
    },
    {
      icon: <Zap className="h-8 w-8" />,
      title: 'Performance Optimization',
      description: 'Speed up your applications and improve user experience with our optimization services.'
    },
    {
      icon: <Users className="h-8 w-8" />,
      title: 'Team Augmentation',
      description: 'Extend your development team with our skilled professionals.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Discovery & Planning',
      description: 'We start by understanding your business goals, target audience, and project requirements through detailed consultation.'
    },
    {
      step: '02',
      title: 'Strategy & Design',
      description: 'Our team develops a comprehensive strategy and creates wireframes, mockups, and prototypes for your approval.'
    },
    {
      step: '03',
      title: 'Development & Testing',
      description: 'We build your solution using agile methodology with regular updates and thorough testing at each stage.'
    },
    {
      step: '04',
      title: 'Launch & Support',
      description: 'After successful deployment, we provide ongoing support, maintenance, and optimization services.'
    }
  ];

  const testimonials = [
    {
      name: 'John Smith',
      company: 'TechStart Inc.',
      text: 'Infurasoft delivered an exceptional mobile app that exceeded our expectations. Their attention to detail and technical expertise is outstanding.',
      rating: 5
    },
    {
      name: 'Sarah Johnson',
      company: 'E-commerce Plus',
      text: 'The web development team created a beautiful, fast, and user-friendly e-commerce platform that significantly boosted our sales.',
      rating: 5
    },
    {
      name: 'Mike Chen',
      company: 'Local Business Co.',
      text: 'Their SEO services helped us rank #1 for our target keywords. Our organic traffic increased by 300% in just 6 months.',
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-12">
          <div className="text-center">
            <div className="mb-4">
              <span className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium text-blue-100 border border-white/30">
                🚀 Digital Solutions
              </span>
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 leading-tight">
              Our <span className="text-blue-300 bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">Services</span>
            </h1>
            <p className="text-lg lg:text-xl mb-8 text-blue-100 leading-relaxed max-w-4xl mx-auto">
              Comprehensive digital solutions to transform your business and drive growth in the digital age. From concept to completion, we've got you covered.
            </p>
            
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mt-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">4</div>
                <div className="text-blue-200 text-sm">Core Services</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">5+</div>
                <div className="text-blue-200 text-sm">Additional Services</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">200+</div>
                <div className="text-blue-200 text-sm">Projects</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
                <div className="text-2xl font-bold">24/7</div>
                <div className="text-blue-200 text-sm">Support</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Services Section */}
      <section className="py-12 bg-gradient-to-br from-white via-gray-50/50 to-blue-50/30 dark:from-gray-900 dark:via-gray-800/50 dark:to-blue-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-200 rounded-full text-sm font-medium mb-6">
              🚀 Core Services
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Our <span className="text-blue-600 dark:text-blue-400">Expertise</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              We specialize in four key areas that drive digital transformation and business growth, delivering exceptional results through innovative solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <div key={service.id} className="group relative bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-2xl shadow-md overflow-hidden hover:shadow-lg transition-all duration-300 border border-gray-100/50 dark:border-gray-700/50 hover:border-blue-200 dark:hover:border-blue-600 hover:-translate-y-1 hover:scale-102 mx-4">
                {/* Header with Gradient */}
                <div className={`bg-gradient-to-r ${service.color} p-6 text-white relative overflow-hidden`}>
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-black/5 transition-colors duration-300"></div>
                  
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-20">
                    <div className="absolute inset-0" style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='20' cy='20' r='2'/%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                  </div>
                  
                  <div className="relative z-10">
                    <div className="flex items-center mb-3">
                      <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl mr-3 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg">
                        {service.icon}
                      </div>
                      <h3 className="text-xl font-bold">{service.title}</h3>
                    </div>
                    <p className="text-xs opacity-90 leading-relaxed">{service.description}</p>
                  </div>
                  
                  {/* Floating Elements */}
                  <div className="absolute top-3 right-3 w-6 h-6 bg-white/20 rounded-full flex items-center justify-center text-white text-xs animate-pulse">
                    ⭐
                  </div>
                  <div className="absolute bottom-3 left-3 w-5 h-5 bg-white/20 rounded-full flex items-center justify-center text-white text-xs animate-bounce">
                    🚀
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="mb-4">
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Key Features:</h4>
                    <div className="grid grid-cols-1 gap-2">
                      {service.features.slice(0, 3).map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">
                          <CheckCircle className="h-3 w-3 text-green-500 mr-2 flex-shrink-0 group-hover:scale-110 transition-transform duration-300" />
                          <span className="text-gray-600 dark:text-gray-300 text-xs">{feature}</span>
                        </div>
                      ))}
                      {service.features.length > 3 && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                          +{service.features.length - 3} more features
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="mb-4">
                    <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-3">Technologies:</h4>
                    <div className="flex flex-wrap gap-1">
                      {service.technologies.slice(0, 3).map((tech, techIndex) => (
                        <span key={techIndex} className="px-2 py-1 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 text-gray-700 dark:text-gray-300 text-xs rounded-full font-medium border border-gray-200/50 dark:border-gray-600/50 group-hover:scale-105 transition-transform duration-300">
                          {tech}
                        </span>
                      ))}
                      {service.technologies.length > 3 && (
                        <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-full font-medium">
                          +{service.technologies.length - 3}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <Link
                    to="/contact"
                    className="group/btn inline-flex items-center justify-center w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold rounded-lg transition-all duration-300 shadow-md hover:shadow-lg text-sm"
                  >
                    <span>Get Started</span>
                    <ArrowRight className="h-3 w-3 ml-2 group-hover/btn:translate-x-1 transition-transform duration-300" />
                  </Link>
                </div>
                
                {/* Hover Effect Border */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500/20 to-indigo-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-xl"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services Section */}
      <section className="py-12 bg-gradient-to-br from-gray-50 via-white to-green-50/30 dark:from-gray-800 dark:via-gray-900 dark:to-green-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-200 rounded-full text-sm font-medium mb-6">
              ⚡ Additional Services
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              Complete <span className="text-green-600 dark:text-green-400">Solutions</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Complementary services to support your complete digital transformation journey and ensure your success at every step.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {additionalServices.map((service, index) => (
              <div key={index} className="group relative bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm p-6 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100/50 dark:border-gray-700/50 hover:border-green-200 dark:hover:border-green-600 hover:-translate-y-1 hover:scale-102 mx-2">
                {/* Background Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-green-50/50 to-transparent dark:from-green-900/20 dark:to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="relative z-10">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-xl mb-4 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg">
                    {service.icon}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-xs leading-relaxed group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">
                    {service.description}
                  </p>
                </div>
                
                {/* Hover Effect Border */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-green-500/20 to-emerald-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-xl"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-12 bg-gradient-to-br from-white via-gray-50/50 to-purple-50/30 dark:from-gray-900 dark:via-gray-800/50 dark:to-purple-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-purple-100 dark:bg-purple-900/50 text-purple-800 dark:text-purple-200 rounded-full text-sm font-medium mb-6">
              🔄 Our Process
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              How We <span className="text-purple-600 dark:text-purple-400">Work</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              A proven methodology that ensures successful project delivery and client satisfaction through systematic approach and continuous collaboration.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {process.map((step, index) => (
              <div key={index} className="text-center relative">
                <div className="relative mb-8">
                  <div className="group inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-600 text-white text-2xl font-bold rounded-full shadow-lg hover:shadow-xl transition-all duration-500 hover:scale-110 hover:rotate-3">
                    {step.step}
                  </div>
                  {index < process.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-full w-full h-1 bg-gradient-to-r from-purple-300 to-indigo-300 dark:from-purple-600 dark:to-indigo-600 -translate-y-0.5 rounded-full"></div>
                  )}
                </div>
                <div className="group bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-500 border border-gray-100/50 dark:border-gray-700/50 hover:border-purple-200 dark:hover:border-purple-600 hover:-translate-y-2">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-300">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 bg-gradient-to-br from-gray-50 via-white to-orange-50/30 dark:from-gray-800 dark:via-gray-900 dark:to-orange-900/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <div className="inline-block px-4 py-2 bg-orange-100 dark:bg-orange-900/50 text-orange-800 dark:text-orange-200 rounded-full text-sm font-medium mb-6">
              💬 Client Testimonials
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
              What Our <span className="text-orange-600 dark:text-orange-400">Clients Say</span>
            </h2>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Don't just take our word for it - hear from our satisfied clients who have experienced exceptional results with our services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="group relative bg-white/90 dark:bg-gray-900/90 backdrop-blur-sm p-6 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 border border-gray-100/50 dark:border-gray-700/50 hover:border-orange-200 dark:hover:border-orange-600 hover:-translate-y-1 hover:scale-102 mx-2">
                {/* Background Gradient */}
                <div className="absolute inset-0 bg-gradient-to-br from-orange-50/50 to-transparent dark:from-orange-900/20 dark:to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                <div className="relative z-10">
                  {/* Quote Icon */}
                  <div className="absolute -top-3 -left-3 w-10 h-10 bg-gradient-to-br from-orange-500 via-orange-600 to-red-700 rounded-full flex items-center justify-center text-white text-lg shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z"/>
                    </svg>
                  </div>
                  
                  {/* Rating Stars - Right Side */}
                  <div className="flex items-center justify-end mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-current group-hover:scale-110 transition-transform duration-300" style={{ animationDelay: `${i * 100}ms`, color: '#FFC938' }} />
                    ))}
                  </div>
                  
                  {/* Testimonial Content */}
                  <p className="text-gray-600 dark:text-gray-300 mb-6 italic text-xs leading-relaxed line-clamp-3 group-hover:text-gray-700 dark:group-hover:text-gray-300 transition-colors duration-300">
                    "{testimonial.text}"
                  </p>
                  
                  {/* Client Info */}
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-600 rounded-full flex items-center justify-center text-white font-bold text-sm group-hover:scale-110 group-hover:rotate-3 transition-all duration-300 shadow-lg">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div className="ml-3">
                      <div className="font-bold text-gray-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors duration-300 text-xs">
                        {testimonial.name}
                      </div>
                      <div className="text-gray-500 dark:text-gray-400 text-xs group-hover:text-gray-600 dark:group-hover:text-gray-300 transition-colors duration-300">
                        {testimonial.company}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Hover Effect Border */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-orange-500/20 to-red-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10 blur-xl"></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-12 bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}></div>
        </div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-white/5 rounded-full blur-2xl animate-bounce"></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white/10 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <span className="inline-block px-6 py-3 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium text-blue-100 border border-white/30 mb-6">
              🚀 Ready to Transform?
            </span>
          </div>
          
          <h2 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Ready to Get <span className="text-blue-300 bg-gradient-to-r from-blue-300 to-cyan-300 bg-clip-text text-transparent">Started?</span>
          </h2>
          <p className="text-xl lg:text-2xl text-blue-100 mb-12 max-w-4xl mx-auto leading-relaxed">
            Let's discuss your project requirements and how we can help bring your vision to life with our expert team and proven solutions.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center mb-12">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center px-10 py-5 text-lg font-semibold rounded-2xl bg-white text-blue-600 hover:bg-blue-50 transition-all duration-300 shadow-2xl hover:shadow-3xl hover:scale-105"
            >
              <span>Start Your Project</span>
            </Link>
            <Link
              to="/about"
              className="group inline-flex items-center justify-center px-10 py-5 text-lg font-semibold rounded-2xl border-2 border-white text-white hover:bg-white hover:text-blue-600 transition-all duration-300 hover:scale-105"
            >
              <span>Learn More About Us</span>
            </Link>
          </div>
          
          {/* Trust Indicators */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">Expert</div>
              <div className="text-blue-200 text-sm">Team</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">Proven</div>
              <div className="text-blue-200 text-sm">Results</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="text-3xl font-bold text-white mb-2">24/7</div>
              <div className="text-blue-200 text-sm">Support</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;